import { useCallback, useEffect, useState } from 'react';
import { ApiError } from './api';

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** Re-runs the loader. */
  reload: () => void;
  /** Replaces the data locally, for optimistic updates after a mutation. */
  setData: (next: T) => void;
}

/**
 * Runs an async loader on mount and whenever `deps` change, with abort handling
 * so a fast navigation does not write stale data into state.
 */
export function useAsync<T>(
  loader: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState(0);

  const reload = useCallback(() => setToken((n) => n + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);

    loader(controller.signal)
      .then((loaded) => {
        if (controller.signal.aborted) return;
        setData(loaded);
        setError(null);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError(err instanceof ApiError ? err.message : 'Something went wrong loading this page.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
    // The loader is intentionally excluded; callers pass their own deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, token]);

  return { data, loading, error, reload, setData };
}
